/**
 * Admission back-pressure: the one place another subsystem can tell the runner "not now".
 *
 * WHAT THIS FILE USED TO BE, because the deletion is the design decision and should not have to be reconstructed
 * from git. It held a heap-reservation system: every job type declared a worst case (`heapCeilingBytes`),
 * admission subtracted that at claim time against a budget derived from `--max-old-space-size`, handlers could
 * hand memory back with `declareHeap` / `updateHeap`, and the model gate measured each payload so a claim could
 * settle toward its real cost. Alongside it: an RSS budget, a memory-pressure threshold, an event-loop check and
 * a process-CPU check.
 *
 * IT IS GONE, and the reasons are worth keeping:
 *
 *  - **The declaration was fiction.** Reservation accounting only works if handlers declare honestly, and two of
 *    ten types ever did. The other eight inherited the maximum, so four jobs declared the entire budget while
 *    genuinely holding one payload each — and the DECLARED term is the one that binds admission, not the measured
 *    one. The system throttled itself to about four concurrent jobs against a model gate provisioned for thirty
 *    concurrent calls.
 *  - **It guarded the wrong resource.** The one OOM this service has had came from rasterising PDF pages, and
 *    those buffers are `external` memory: `--max-old-space-size` does not govern them and `getHeapStatistics()`
 *    cannot see them — nor can it see a worker thread's isolate. The budget could not observe the failure it was
 *    introduced to prevent.
 *  - **It was the wrong altitude.** The real fan-outs are inside handlers: fifteen concurrent page conversions,
 *    an OCR call per region of an entire task tree. A job-level budget admits one job and then watches it fan
 *    out. Both are bounded now where they happen, by `createLimiter` in `db/utils/concurrency.ts` — a number
 *    about the operation instead of a guess about the caller.
 *  - **The CPU checks did net harm.** `processCpuCheck` was not pool-scoped, so one saturating extraction refused
 *    admission to every other type, which is what made a four-wide queue behave like a one-wide one.
 *
 * WHAT REPLACED IT: `JOB_CONCURRENCY`, one integer in `limits.ts`; limiters at the allocation sites; and accepting
 * that the process can die while making that cheap — leases, an attempt refund when the worker was killed rather
 * than at fault (`sweeper.ts`), and a hand-back of this host's claims on boot (`handBackOwnOrphans`). The
 * long-term fix is a separate worker process, so a death costs retried jobs instead of the teacher's open
 * subscriptions.
 *
 * WHAT SURVIVED: this registry. The model gate registers a check here so a deep provider queue stops the runner
 * from making it deeper — a MEASURED signal about a resource we do not own, which is the one shape of
 * back-pressure the deleted system never got wrong.
 */

import { logger } from '@adornis/base/logging.js';
import type { JobPool } from './job.js';

/** What is asking to be admitted. `type` is carried for custom checks and for the refusal message. */
export interface AdmissionCandidate {
  pool: JobPool;
  type: string;
}

/**
 * Returns a reason to refuse admission, or `null` to allow it.
 *
 * TAKES NO LOAD SAMPLE, unlike the version this replaced. A check that needs a measurement should take its own:
 * the runner sampling the event loop, process CPU and memory every tick, for checks that mostly ignored it, was
 * pure cost. A check must be cheap and synchronous — it runs per candidate job.
 */
export type AdmissionCheck = (candidate: AdmissionCandidate) => string | null;

const checks: AdmissionCheck[] = [];

/**
 * Registers a back-pressure signal. Intended for the model client: when its shared call limiter is saturated,
 * refusing `io` admission stops the runner turning a provider queue into a deeper one while holding every queued
 * job's payload in memory.
 */
export function addJobAdmissionCheck(check: AdmissionCheck): void {
  checks.push(check);
}

export function admissionRefusal(candidate: AdmissionCandidate): string | null {
  for (const check of checks) {
    try {
      const refusal = check(candidate);
      if (refusal) return refusal;
    } catch (err) {
      // A broken signal must not stop the runner; the remaining checks still apply.
      logger.error({ err, candidate }, 'Job admission check threw — ignoring it');
    }
  }
  return null;
}
